import type { MetaFunction } from "react-router"

import { ButtonLink } from "~/components/ui/button"
import { DocsBanner } from "~/components/landing/DocsBanner"
import { BlogLayout, BlogProse } from "~/components/blog/BlogLayout"
import { BlogCta } from "~/components/blog/BlogCta"
import { WrittenBy } from "~/components/blog/WrittenBy"
import {
  A,
  H2,
  H3,
  Lead,
  List,
  P,
  Quote,
  Strong,
  Table,
  VideoFigure,
} from "~/components/blog/prose"
import { getPost, postMeta } from "~/data/posts"

// The animation is rendered from uc/foot-traffic-explainer (an SVG/JS
// simulation captured to MP4 + GIF). The copy is mirrored in
// uc/foot-traffic-explainer/Draft.md, where edits are made and then ported
// here; keep the two in sync.

const GUIDES = "https://console.pine59.com/docs/guides"
const CONCEPTS = "https://console.pine59.com/docs/concepts"
const LINKS = {
  place: `${CONCEPTS}/location-types/place`,
  area: `${CONCEPTS}/location-types/area`,
  placeFootTraffic: `${CONCEPTS}/metrics/foot-traffic-place`,
  areaFootTraffic: `${CONCEPTS}/metrics/foot-traffic-area`,
  tradeArea: `${CONCEPTS}/metrics/trade-area-place`,
  visitorDemographics: `${CONCEPTS}/metrics/visitor-demographics-place`,
  visitorProfile: `${CONCEPTS}/metrics/places_spatialai_visitor_profile`,
  visitorJourney: `${CONCEPTS}/metrics/places_visitor_journey`,
}
const ANIM =
  "/art/blog/what-is-foot-traffic/visits-arrivals-departures-attendance"

const post = getPost("what-is-foot-traffic")!

export const meta: MetaFunction = ({ location }) =>
  postMeta(post, location.pathname)

// "Guides: A, B." closing a use-case section.
function Guides({ links }: { links: { label: string; slug: string }[] }) {
  return (
    <p className="mt-4 text-[16px] leading-relaxed text-mute">
      {links.length > 1 ? "Guides: " : "Guide: "}
      {links.map((l, i) => (
        <span key={l.slug}>
          {i > 0 ? ", " : null}
          <A href={`${GUIDES}/${l.slug}`}>{l.label}</A>
        </span>
      ))}
      .
    </p>
  )
}

export default function Post() {
  return (
    <BlogLayout post={post}>
      <BlogProse>
        <WrittenBy post={post} />

        <Lead>
          Foot traffic is the number of visits to a location over a period of
          time. A visit is one person spending time at a location, whether a
          bounded area or a specific place. Add the visits up over a day, a
          week, or a month and you have the foot traffic for that period.
        </Lead>
        <P>
          That is the whole definition, and it applies to a single store as well
          as to a park, a neighborhood, or a high street. What makes foot
          traffic useful is that the same number can be read in several ways:
          how much activity a place gets, when it gets it, how that compares
          with last year or with the place down the road. With related metrics
          it also tells who the people are and where they come from.
        </P>

        <BlogCta />

        <H2>What foot traffic measures</H2>
        <P>
          The animation below follows one grocery store through a day. Each dot
          is a person. A dot is counted the moment it crosses the location
          boundary.
        </P>
        <P>
          What foot traffic reports is the statistics on the right. Pine59 does
          not report on, and cannot report on, any single dot in this animation.
        </P>
        <VideoFigure
          src={`${ANIM}.mp4`}
          poster={`${ANIM}-poster.png`}
          fallback={`${ANIM}.gif`}
          width={960}
          height={540}
          label="Animation of a grocery store over one day. People drive in, park, walk in through the door, shop, and leave, while counters for visits, attendance, arrivals and departures update."
          caption="One simulated day at a grocery store. Gray dots are outside the location boundary and not counted, green dots are inside and counted."
        />
        <P>Four numbers move on the right, and it helps to keep them apart:</P>
        <Table
          head={["Measure", "The question it answers", "In the animation"]}
          rows={[
            [
              <Strong key="v">Visits</Strong>,
              "How many people came during the period?",
              "The running total for the day",
            ],
            [
              <Strong key="a">Arrivals</Strong>,
              "How many came in during this hour?",
              "Resets at the top of every hour",
            ],
            [
              <Strong key="d">Departures</Strong>,
              "How many left during this hour?",
              "Resets at the top of every hour",
            ],
            [
              <Strong key="t">Attendance</Strong>,
              "How many are inside right now?",
              "The snapshot count",
            ],
          ]}
        />
        <P>
          Of the four values above, visits hold the information required for the
          vast majority of use cases, and is the metric value which Pine59
          focuses on and emphasizes.
        </P>

        <H2>Foot traffic for places</H2>
        <P>
          A <A href={LINKS.place}>place</A> is a store, a restaurant, a venue: a
          location with a name, a category, and a boundary polygon. Place foot
          traffic is the estimated visits to that exact context, not only its
          polygon, so it can be read against the place&rsquo;s own history,
          against the rest of its chain, against its category, and against named
          competitors, all on the same definition.
        </P>
        <P>
          The higher the volume of visits, the more resolution we have in the
          measurement. Expanding the time period improves resolution for many
          lower-volume locations. In dense environments, several places share a
          street or a building, and attributing a visit to exactly the right one
          is not perfect. We use a knowledge-based model to improve performance
          on individual locations, aimed specifically at dense and low-traffic
          places. In those settings the model is typically best at places whose
          traffic pattern largely follows their immediate area.
        </P>
        <P>
          Visits to a place can also be counted at the door, with beam, camera,
          or Wi-Fi counters. Those are precise for the doors they sit on and say
          nothing about the store across the street, the mall, or the
          neighborhood. Location intelligence estimates visits to every place
          and area with one method and one definition, including the ones nobody
          has instrumented, and is less exact for a single door than a counter
          is. The two are complementary: counters tell you what happened at your
          own doors, location intelligence puts that in context and extends the
          same measurement to everywhere you do not own.
        </P>

        <H2>Foot traffic for areas</H2>
        <P>
          The same measure applies to an <A href={LINKS.area}>area</A>: a park,
          a neighborhood, a downtown, a high street. Here foot traffic describes
          the volume, timing, and intensity of activity in a space with no
          doors. It shows whether the area is attracting more people than
          before, how it compares with other areas, how much activity a specific
          event drew, how seasons differ, and, within the week, when the area is
          busy and when it is slow.
        </P>
        <P>
          For areas, the boundary is the analyst&rsquo;s choice. Neighborhood
          grids, administrative districts, and custom polygons are all valid
          locations. Because an area counts all activity inside its polygon, its
          absolute counts are steadier than a single place&rsquo;s.
        </P>

        <H2>Where foot traffic is used</H2>

        <H3>Retail performance and competitive intelligence</H3>
        <P>
          For a store, restaurant, or service business, visits over time are a
          direct performance indicator, and one that exists for competitors too.
          The typical reads are a store against its own history, against the
          rest of the chain, against the category, and against named competitors
          on identical windows. Portfolio views rank stores by visits and by
          trend, and single-store profiles explain why a location behaves the
          way it does.
        </P>
        <Guides
          links={[
            { label: "Brand Comparison", slug: "brand-comparison" },
            { label: "Store Profiling", slug: "store-profiling" },
          ]}
        />

        <H3>Site selection</H3>
        <P>
          Before a lease is signed, foot traffic is the demand half of the
          decision. It screens a whole market for areas with the activity you
          want, finds locations that look like the ones where you already win,
          and tests what an area lacks that its demand could support. Measured
          movement beats resident statistics here because it captures what a
          place attracts, not only who lives next to it.
        </P>
        <Guides
          links={[
            { label: "Area Identification", slug: "area-identification" },
            { label: "Analog Modeling", slug: "analog-modeling" },
            { label: "Void Analysis", slug: "void-analysis" },
          ]}
        />

        <H3>Cities and urban development</H3>
        <P>
          For municipalities, business improvement districts, and developers,
          area foot traffic is the reporting layer. It tracks a district before
          and after an intervention against the rest of the city, measures event
          impact, and surfaces emerging areas: places where activity is growing
          faster than their surroundings before that shows up anywhere else.
        </P>
        <Guides
          links={[
            {
              label: "Area Development Reporting",
              slug: "area-development-reporting",
            },
          ]}
        />
      </BlogProse>

      <div className="mt-14">
        <DocsBanner body="Every guide above ends in a worked example with real results. Explore the Pine59 Console & API docs: guides, examples, datasets, concepts, and APIs." />
      </div>

      <BlogProse>
        <H2>
          Foot traffic says what happened. Related metrics say why and how.
        </H2>
        <P>
          Foot traffic on its own tells you how many and when. The metrics built
          on the same observations explain the number:
        </P>
        <List
          items={[
            <>
              <A href={LINKS.tradeArea}>Trade areas</A> show where visitors come
              from, and whether a place serves its neighborhood or draws from
              across the metro.
            </>,
            <>
              <A href={LINKS.visitorDemographics}>Visitor demographics</A> and{" "}
              <A href={LINKS.visitorProfile}>visitor profiles</A> describe the
              audience a place attracts, based on visitors rather than
              residents.
            </>,
            <>
              <A href={LINKS.visitorJourney}>Visitor journeys</A> show which
              other places are part of the same trip, including competitors
              visited on the same day.
            </>,
            <>
              <Strong>Popular times</Strong> give the busy-hours profile of a
              typical week.
            </>,
          ]}
        />
        <Quote>
          A drop in visits is a fact. The trade area shrinking on one side,
          or a competitor entering the same journeys, is the explanation.
        </Quote>

        <H2>How Pine59 models foot traffic</H2>
        <P>
          Foot traffic is built in three steps. The details differ by model and
          market: place foot traffic in the US, for example, is a
          knowledge-based machine-learning model in which pre-aggregated signal
          observations are one of many feature inputs. The three steps are the
          shape they share.
        </P>
        <P>
          <Strong>Observation.</Strong> A panel of devices shares location
          signals. When a device is inside a location&rsquo;s boundary for a
          sustained period, that is a visit. Passing by is not a visit, and
          neither is a single momentary ping. For Pine59, this step happens
          inside the first-party data owner: the app publisher for GPS location
          data, the mobile operator for network data. Individual signals stay in
          their clean rooms, and what reaches us is already aggregated.
        </P>
        <P>
          <Strong>One person, one visit.</Strong> Visits are counted per person
          per day. Someone who comes in twice on the same day is one visit that
          day. The goal is a count of people, treated as interchangeable: the
          metric says how many, not who.
        </P>
        <P>
          <Strong>Extrapolation.</Strong> The panel is a sample. Counts are
          scaled to represent the full population, so a foot traffic number
          reads as an estimate of real-world visits, not as a count of observed
          devices. The bigger the crowd, the smaller the relative error band: a
          busy place or a whole area is estimated tightly, a low-traffic place
          on a single day is not, and for those the monthly read is the one to
          trust. Two consequences follow. Directional trends and rankings are
          more accurate than absolute counts. And absolute counts are better for
          areas, which capture all activity within a polygon, than for a
          specific store, where size and the density of its surroundings matter.
        </P>
        <P>
          The most robust read of all is the relation between two locations in
          the same region or sub-market over the same period. A change in that
          ratio is typically a very strong signal, because everything the panel
          adds affects both sides alike.
        </P>

        <H2>Foot traffic from Pine59</H2>
        <P>
          We offer aggregated location intelligence foot traffic as two metrics,
          one per location type.
        </P>
        <List
          items={[
            <>
              <A href={LINKS.placeFootTraffic}>Place foot traffic</A>: visits
              per store or venue in the US at daily, weekly, and monthly
              cadence, with trade areas, visitor profiles, journeys, and popular
              times on the same locations.
            </>,
            <>
              <A href={LINKS.areaFootTraffic}>Area foot traffic</A>: visits to
              neighborhoods, districts, and custom polygons in the US and the
              Nordics, with visitors split into residents, workers, and other
              visitors where the signal supports it.
            </>,
          ]}
        />
        <P>
          The data is available through the API, in bulk exports, and in Console
          without code. Our docs open with{" "}
          <A href={`${GUIDES}/first-query`}>Make your first query</A> and{" "}
          <A href={`${GUIDES}/no-code`}>Use Pine59 without code</A>, and every
          guide linked above ends in a worked example with real results.
        </P>
        <P>
          Want to see what foot traffic shows for your locations or markets?
        </P>
        <div className="mt-8">
          <ButtonLink href="/get-started" withArrow>
            Book a meeting
          </ButtonLink>
        </div>
      </BlogProse>
    </BlogLayout>
  )
}
